// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { Badge, Box, Container, Divider, Flex, Group, Stack, Sx, Text } from '@mantine/core';
import { ElementType, ReactNode, useContext, useEffect, useState } from 'react';
import {
  IconBriefcase,
  IconCamera,
  IconDeviceSpeaker,
  IconHeadphones,
  IconPhoto,
  IconToolsKitchen,
} from '@tabler/icons';
import { useJwtClaims } from '../../../Service/Context/AuthContext';
import { LicenceS } from '../../../Service/helper/LicenceService';
import { useUpdateBillingPlans } from '../../../Service/hooks/useUpdateBillingPlans';
import { RoutingS } from '../../../Service/RoutingS';
import { BillingInterval } from '../../../Types/Billing';
import BookitupSegmentedControl from '../../../Atoms/BookitupSegmentedControl';
import { DataS } from '../../../Service/DataS';
import { WindowContext } from '../../../Service/Context/WindowContext';
import { useBookitupSubscription } from '../../../Service/Context/BookitupSubscriptionContext';
import { ThemeText } from '../../../Atoms/ThemeText';
import PanePaper from '../../../Molecules/PanePaper';
import AvailableAddons from './AvailableAddons';
import AvailablePlans from './AvailablePlans';
import SubscriptionPane from './SubscriptionPane';
import TrialRedeemAlert from './TrialRedeemAlert';
// eslint-disable-next-line import/no-unresolved
import 'swiper/css';
// eslint-disable-next-line import/no-unresolved
import 'swiper/css/navigation';
// eslint-disable-next-line import/no-unresolved
import 'swiper/css/pagination';

type PackageSectionView = 'plans' | 'addons';

const packageSectionViews: { label: ReactNode; value: PackageSectionView }[] = [
  { label: 'Pakete', value: 'plans' },
  { label: 'Add-ons', value: 'addons' },
];

const getServiceBadgeLabel = (value: string) =>
  DataS.servicesOptions.find((option) => option.value === value)?.label ?? value;

const packageServiceBadges: { label: string; icon: ElementType; color: string }[] = [
  { label: getServiceBadgeLabel('Fotograf:in'), icon: IconCamera, color: 'pink' },
  { label: getServiceBadgeLabel('DJ'), icon: IconHeadphones, color: 'grape' },
  { label: getServiceBadgeLabel('Fotobox-Verleih'), icon: IconPhoto, color: 'teal' },
  { label: getServiceBadgeLabel('Catering'), icon: IconToolsKitchen, color: 'orange' },
  { label: getServiceBadgeLabel('Eventtechnik'), icon: IconDeviceSpeaker, color: 'cyan' },
  { label: getServiceBadgeLabel('Agentur'), icon: IconBriefcase, color: 'gray' },
];

const segmentedControlSx: Sx = (theme) => ({
  width: '100%',
  maxWidth: '100%',
  padding: 3,
  backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[8] : theme.white,
  '.mantine-SegmentedControl-control': {
    minWidth: 0,
  },
  '.mantine-SegmentedControl-label': {
    minHeight: 30,
    minWidth: 0,
    paddingLeft: 8,
    paddingRight: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 800,
    fontSize: 12,
    textAlign: 'center',
    lineHeight: 1.15,
    whiteSpace: 'normal',
  },
  '.mantine-SegmentedControl-active': {
    boxShadow: theme.colorScheme === 'dark' ? '0 5px 12px rgba(0, 0, 0, 0.28)' : '0 5px 12px rgba(29, 81, 145, 0.12)',
  },
  '@media (max-width: 380px)': {
    padding: 2,
    '.mantine-SegmentedControl-label': {
      minHeight: 28,
      paddingLeft: 5,
      paddingRight: 5,
      fontSize: 11,
    },
  },
});

const PackageSection = () => {
  const jwtClaims = useJwtClaims();
  const { subscription } = useBookitupSubscription();
  const [billingInterval, setBillingInterval] = useState<BillingInterval>('Yearly');
  const [view, setView] = useState<PackageSectionView>('plans');
  const { sm } = useContext(WindowContext);
  const { licence } = jwtClaims;
  const freeLicence = LicenceS.isFreeLicence(licence);
  const hasActiveSubscription = subscription?.status === 'ACTIVE';
  const selectedPackageSectionViewLabel = packageSectionViews.find((option) => option.value === view)?.label;
  const billingIntervalOptions = DataS.billingIntervals.map((option) => {
    if (option.value !== 'Yearly' || hasActiveSubscription) {
      return option;
    }

    return {
      ...option,
      label: (
        <Group spacing={5} noWrap>
          <Text span size={12} weight={800} sx={{ color: 'inherit', lineHeight: 1 }}>
            {option.label}
          </Text>
          <Text span size={10} weight={900} sx={{ color: '#FE8047', lineHeight: 1 }}>
            -15%
          </Text>
        </Group>
      ),
    };
  });

  useUpdateBillingPlans(freeLicence);

  useEffect(() => {
    RoutingS.changeTitle('Paket');
  }, []);

  useEffect(() => {
    if (subscription) {
      if (subscription.interval !== billingInterval) {
        setBillingInterval(subscription.interval);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscription]);

  return (
    <Container p={0}>
      <SubscriptionPane />
      <PanePaper>
        <Flex
          gap={sm ? 'sm' : 'lg'}
          align={sm ? 'stretch' : 'flex-start'}
          direction={sm ? 'column' : 'row'}
          sx={{ minWidth: 0 }}
        >
          <Box sx={{ flex: sm ? '1 1 auto' : '1 1 360px', minWidth: 0 }}>
            <ThemeText my={4} size={sm ? 19 : 24} weight={900}>
              360° Eventverwaltung mit bookitup
            </ThemeText>
            <Text color="dimmed" my="md" size={sm ? 'xs' : 'sm'} sx={{ maxWidth: 500, overflowWrap: 'break-word' }}>
              Du bezahlst nur, was Du brauchst (und wenn du es brauchst)
            </Text>
            <Group mt="sm" spacing={6} sx={{ rowGap: 6 }}>
              {packageServiceBadges.map((badge) => {
                const ServiceIcon = badge.icon;

                return (
                  <Badge
                    key={badge.label}
                    size="sm"
                    radius="sm"
                    color={badge.color}
                    variant="light"
                    sx={(theme) => ({
                      height: 24,
                      paddingLeft: 8,
                      paddingRight: 9,
                      textTransform: 'none',
                      color: theme.colorScheme === 'dark' ? theme.colors[badge.color][2] : theme.colors[badge.color][8],
                    })}
                  >
                    <Group spacing={4} noWrap>
                      <ServiceIcon size={13} stroke={2.2} />
                      <Text span size={11} weight={700} sx={{ lineHeight: 1 }}>
                        {badge.label}
                      </Text>
                    </Group>
                  </Badge>
                );
              })}
            </Group>
          </Box>
          {sm ? <Box h="xs" /> : <Divider orientation="vertical" sx={{ alignSelf: 'stretch' }} />}
          <Stack
            spacing="xs"
            sx={{
              flex: sm ? '1 1 auto' : '0 0 300px',
              width: '100%',
              maxWidth: sm ? '100%' : 320,
            }}
          >
            <Box>
              <Text mb={4} size={10} color="dimmed" weight={800} transform="uppercase">
                Anzeigen
              </Text>
              <BookitupSegmentedControl
                value={view}
                size="xs"
                data={packageSectionViews}
                onChange={(val: PackageSectionView) => val && setView(val)}
                sx={segmentedControlSx}
              />
            </Box>
            <Box>
              <Group
                position="apart"
                spacing={6}
                mb={4}
                sx={{
                  alignItems: 'flex-start',
                  rowGap: 4,
                }}
              >
                <Text size={10} color="dimmed" weight={800} transform="uppercase" sx={{ minWidth: 0 }}>
                  Abrechnung
                </Text>
              </Group>
              <BookitupSegmentedControl
                size="xs"
                value={billingInterval}
                data={billingIntervalOptions}
                onChange={(val: BillingInterval) => val && setBillingInterval(val)}
                sx={segmentedControlSx}
              />
            </Box>
          </Stack>
        </Flex>
      </PanePaper>
      <Flex gap="md" direction={sm ? 'column' : 'row'} align="flex-start">
        <Box mt="xl" sx={{ flex: '1 1 650px', minWidth: 0, width: '100%' }}>
          <Divider mb="xl" label={<ThemeText>{selectedPackageSectionViewLabel}</ThemeText>} labelPosition="center" />
          {view === 'plans' && <AvailablePlans billingInterval={billingInterval} />}
          {view === 'addons' && <AvailableAddons billingInterval={billingInterval} />}
        </Box>
      </Flex>
      <Stack sx={{ flex: sm ? '1 1 auto' : '0 0 360px', maxWidth: sm ? '100%' : 450, width: '100%' }}>
        {LicenceS.trialRedeemable(jwtClaims) && <TrialRedeemAlert />}
      </Stack>
    </Container>
  );
};

export default PackageSection;
